import React, { useState, useEffect } from 'react';
import { commissionService } from '../../../../services/commissionService';
import { FaMoneyBillWave, FaPercentage, FaCheckCircle, FaTimesCircle, FaClock, FaFileExport, FaPlus, FaEdit, FaTrash, FaEye, FaRupeeSign, FaCalendarAlt, FaFilter } from 'react-icons/fa';

const Commissions = () => {
  const [activeTab, setActiveTab] = useState('rates'); // rates, commissions, payouts
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({});
  const [payoutStats, setPayoutStats] = useState({});
  const [selectedRate, setSelectedRate] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showRateModal, setShowRateModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showCreatePayoutModal, setShowCreatePayoutModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    vendorId: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'rates') {
        const data = await commissionService.getCommissionRates();
        setRates(Array.isArray(data) ? data : []);
      } else if (activeTab === 'commissions') {
        const [commData, statsData] = await Promise.all([
          commissionService.getAllCommissions(filters),
          commissionService.getCommissionStats(filters)
        ]);
        setCommissions(Array.isArray(commData) ? commData : []);
        setStats(statsData || {});
      } else if (activeTab === 'payouts') {
        const [payoutData, statsData] = await Promise.all([
          commissionService.getAllPayouts(filters),
          commissionService.getPayoutStats(filters)
        ]);
        setPayouts(Array.isArray(payoutData) ? payoutData : []);
        setPayoutStats(statsData || {});
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRate = async (rateData) => {
    try {
      await commissionService.createCommissionRate(rateData);
      setShowRateModal(false);
      setSelectedRate(null);
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleUpdateRate = async (id, rateData) => {
    try {
      await commissionService.updateCommissionRate(id, rateData);
      setShowRateModal(false);
      setSelectedRate(null);
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeleteRate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this commission rate?')) return;
    try {
      await commissionService.deleteCommissionRate(id);
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCreatePayout = async (payoutData) => {
    try {
      await commissionService.createPayout(payoutData);
      setShowCreatePayoutModal(false);
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleProcessPayout = async (id, transactionId) => {
    try {
      await commissionService.processPayout(id, transactionId);
      setShowPayoutModal(false);
      setSelectedPayout(null);
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleUpdatePayoutStatus = async (id, status, transactionId = null, failureReason = null) => {
    try {
      await commissionService.updatePayoutStatus(id, { status, transactionId, failureReason });
      setShowPayoutModal(false);
      setSelectedPayout(null);
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleExportPayouts = async () => {
    try {
      const blob = await commissionService.exportPayouts(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payouts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert(error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      Pending: 'bg-yellow-100 text-yellow-800',
      Approved: 'bg-blue-100 text-blue-800',
      Paid: 'bg-green-100 text-green-800',
      Processing: 'bg-purple-100 text-purple-800',
      Failed: 'bg-red-100 text-red-800',
      Cancelled: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Commission Management</h1>
        <div className="flex gap-2">
          {activeTab === 'payouts' && (
            <>
              <button
                onClick={() => setShowCreatePayoutModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <FaPlus /> Create Payout
              </button>
              <button
                onClick={handleExportPayouts}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <FaFileExport /> Export
              </button>
            </>
          )}
          {activeTab === 'rates' && (
            <button
              onClick={() => {
                setSelectedRate(null);
                setShowRateModal(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <FaPlus /> Add Rate
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {['rates', 'commissions', 'payouts'].map((tab, index) => (
            <button
              key={`tab-${tab}-${index}`}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Stats Cards */}
      {activeTab === 'commissions' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Total Commissions</div>
            <div className="text-2xl font-bold mt-2">{stats.total || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-2xl font-bold mt-2 text-yellow-600">{stats.pending || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Total Amount</div>
            <div className="text-2xl font-bold mt-2">{formatCurrency(stats.totalCommissionAmount)}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Pending Amount</div>
            <div className="text-2xl font-bold mt-2 text-yellow-600">{formatCurrency(stats.totalPendingAmount)}</div>
          </div>
        </div>
      )}

      {activeTab === 'payouts' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Total Payouts</div>
            <div className="text-2xl font-bold mt-2">{payoutStats.total || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-2xl font-bold mt-2 text-yellow-600">{payoutStats.pending || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Total Paid</div>
            <div className="text-2xl font-bold mt-2 text-green-600">{formatCurrency(payoutStats.totalPaid)}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Pending Amount</div>
            <div className="text-2xl font-bold mt-2 text-yellow-600">{formatCurrency(payoutStats.totalPending)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      {(activeTab === 'commissions' || activeTab === 'payouts') && (
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Paid">Paid</option>
              {activeTab === 'payouts' && (
                <>
                  <option value="Processing">Processing</option>
                  <option value="Failed">Failed</option>
                </>
              )}
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="px-4 py-2 border rounded-lg"
              placeholder="Start Date"
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="px-4 py-2 border rounded-lg"
              placeholder="End Date"
            />
            <button
              onClick={() => setFilters({ status: 'all', vendorId: '', startDate: '', endDate: '' })}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {activeTab === 'rates' && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rates.map(rate => (
                  <tr key={rate._id}>
                    <td className="px-6 py-4 whitespace-nowrap">{rate.vendorId?.name || 'Default'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{rate.categoryId?.name || 'All'}</td>
                    <td className="px-6 py-4 whitespace-nowrap capitalize">{rate.tier || 'default'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {rate.rateType === 'percentage' ? `${rate.rate}%` : formatCurrency(rate.fixedAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap capitalize">{rate.rateType}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${rate.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {rate.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedRate(rate);
                          setShowRateModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteRate(rate._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {activeTab === 'commissions' && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {commissions.map(commission => (
                  <tr key={commission._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{commission.commissionId}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{commission.vendorId?.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{commission.orderId}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(commission.orderAmount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{commission.commissionRate}%</td>
                    <td className="px-6 py-4 whitespace-nowrap font-semibold">{formatCurrency(commission.commissionAmount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(commission.status)}`}>
                        {commission.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          // View commission details
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {activeTab === 'payouts' && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payout ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payouts.map(payout => (
                  <tr key={payout._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{payout.payoutId}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{payout.vendorId?.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {payout.period?.startDate ? new Date(payout.period.startDate).toLocaleDateString() : 'N/A'} - {payout.period?.endDate ? new Date(payout.period.endDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(payout.totalAmount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap font-semibold">{formatCurrency(payout.netAmount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(payout.status)}`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedPayout(payout);
                          setShowPayoutModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Rate Modal */}
      {showRateModal && (
        <RateModal
          rate={selectedRate}
          onClose={() => {
            setShowRateModal(false);
            setSelectedRate(null);
          }}
          onSave={selectedRate ? handleUpdateRate : handleCreateRate}
        />
      )}

      {/* Payout Detail Modal */}
      {showPayoutModal && selectedPayout && (
        <PayoutDetailModal
          payout={selectedPayout}
          onClose={() => {
            setShowPayoutModal(false);
            setSelectedPayout(null);
          }}
          onProcess={handleProcessPayout}
          onUpdateStatus={handleUpdatePayoutStatus}
        />
      )}

      {/* Create Payout Modal */}
      {showCreatePayoutModal && (
        <CreatePayoutModal
          onClose={() => setShowCreatePayoutModal(false)}
          onCreate={handleCreatePayout}
        />
      )}
    </div>
  );
};

// Rate Modal Component
const RateModal = ({ rate, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    vendorId: rate?.vendorId?._id || '',
    categoryId: rate?.categoryId?._id || '',
    rate: rate?.rate || 15,
    rateType: rate?.rateType || 'percentage',
    fixedAmount: rate?.fixedAmount || 0,
    tier: rate?.tier || 'default',
    minOrderValue: rate?.minOrderValue || 0,
    maxOrderValue: rate?.maxOrderValue || '',
    isActive: rate?.isActive !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(rate?._id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">{rate ? 'Edit' : 'Create'} Commission Rate</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Rate Type</label>
            <select
              value={formData.rateType}
              onChange={(e) => setFormData({ ...formData, rateType: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
          </div>
          {formData.rateType === 'percentage' ? (
            <div>
              <label className="block text-sm font-medium mb-1">Rate (%)</label>
              <input
                type="number"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
                min="0"
                max="100"
                step="0.1"
                required
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Fixed Amount (₹)</label>
              <input
                type="number"
                value={formData.fixedAmount}
                onChange={(e) => setFormData({ ...formData, fixedAmount: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
                min="0"
                step="0.01"
                required
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Tier</label>
            <select
              value={formData.tier}
              onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="default">Default</option>
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Min Order Value (₹)</label>
            <input
              type="number"
              value={formData.minOrderValue}
              onChange={(e) => setFormData({ ...formData, minOrderValue: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded-lg"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Max Order Value (₹)</label>
            <input
              type="number"
              value={formData.maxOrderValue}
              onChange={(e) => setFormData({ ...formData, maxOrderValue: e.target.value ? parseFloat(e.target.value) : null })}
              className="w-full px-3 py-2 border rounded-lg"
              min="0"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="mr-2"
            />
            <label className="text-sm font-medium">Active</label>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Payout Detail Modal
const PayoutDetailModal = ({ payout, onClose, onProcess, onUpdateStatus }) => {
  const [transactionId, setTransactionId] = useState('');
  const [status, setStatus] = useState(payout.status);
  const [failureReason, setFailureReason] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Payout Details</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Payout ID</label>
              <div className="mt-1">{payout.payoutId}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <div className="mt-1">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  payout.status === 'Paid' ? 'bg-green-100 text-green-800' :
                  payout.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                  payout.status === 'Failed' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {payout.status}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Vendor</label>
              <div className="mt-1">{payout.vendorId?.name || 'N/A'}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Period</label>
              <div className="mt-1">
                {payout.period?.startDate ? new Date(payout.period.startDate).toLocaleDateString() : 'N/A'} - {payout.period?.endDate ? new Date(payout.period.endDate).toLocaleDateString() : 'N/A'}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Total Amount</label>
              <div className="mt-1 font-semibold">{formatCurrency(payout.totalAmount)}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Net Amount</label>
              <div className="mt-1 font-semibold text-green-600">{formatCurrency(payout.netAmount)}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Platform Fee</label>
              <div className="mt-1">{formatCurrency(payout.platformFee)}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Tax Deduction</label>
              <div className="mt-1">{formatCurrency(payout.taxDeduction)}</div>
            </div>
          </div>

          {payout.status === 'Pending' && (
            <div className="border-t pt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter transaction ID"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onProcess(payout._id, transactionId)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Process Payout
                </button>
                <button
                  onClick={() => onUpdateStatus(payout._id, 'Failed', null, 'Cancelled by admin')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Mark as Failed
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Payout Modal
const CreatePayoutModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    vendorId: '',
    startDate: '',
    endDate: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Create Payout</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Vendor ID</label>
            <input
              type="text"
              value={formData.vendorId}
              onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Enter vendor ID"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              required
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Commissions;

